import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Stack,
} from '@mui/material';
import { Cookie } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Verificar si el usuario ya ha aceptado las cookies
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Mostrar banner después de un pequeño delay para mejor UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setShowBanner(false);
  };

  const handleReject = () => {
    // Aunque rechace, guardamos la preferencia para no mostrar el banner constantemente
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    setShowBanner(false);
    // Nota: En una implementación completa, aquí se deshabilitarían las cookies no esenciales
  };

  const handlePrivacyPolicy = () => {
    navigate('/privacy-policy');
    setShowBanner(false);
  };

  const handleCookiePolicy = () => {
    navigate('/cookie-policy');
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        p: { xs: 2, sm: 3 },
        borderRadius: 0,
        borderTop: '2px solid',
        borderColor: 'primary.main',
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Cookie sx={{ color: 'primary.main', fontSize: 22.4 }} />
          <Box>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              {t('cookies.title', 'Uso de Cookies')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('cookies.message',
                'Utilizamos cookies esenciales para autenticación y seguridad. Al continuar, acepta nuestro uso de cookies.')}
              {' '}
              <Link
                component="button"
                variant="body2"
                onClick={handlePrivacyPolicy}
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {t('cookies.privacyPolicy', 'Política de Privacidad')}
              </Link>
              {' '}
              {t('cookies.and', 'y')}
              {' '}
              <Link
                component="button"
                variant="body2"
                onClick={handleCookiePolicy}
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {t('cookies.cookiePolicy', 'Política de Cookies')}
              </Link>
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReject}
            sx={{ minWidth: 80 }}
          >
            {t('cookies.reject', 'Rechazar')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAccept}
            startIcon={<Cookie />}
            sx={{ minWidth: 96 }}
          >
            {t('cookies.accept', 'Aceptar')}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

